import React from "react";
import { Star, MapPin, ExternalLink } from "lucide-react";

/**
 * Yastık Medya office map — kreatif edition.
 *
 * Layered composition:
 *   1. Google Maps iframe (z-0) with a multiply pink/teal blend overlay (z-10)
 *   2. Animated conic-gradient border ring (CSS — `map-border-spin` keyframe)
 *   3. Custom marker overlay anchored on office coordinates: pink badge with rocket
 *      glyph + 3 expanding pulse rings + soft drop shadow
 *   4. Floating sticker rozetler ("Beşiktaş HQ", "5.0 ★ Google Reviews")
 *   5. Hover tilt + zoom (CSS transform)
 *
 * Coordinates: Nispetiye Mah. Aytar Cad. Bilgin Sok. — 41.0723°N, 29.0167°E
 *
 * Click → opens the public Google Maps profile in a new tab.
 */
export default function MapEmbed({
  height = 280,
  className = "",
  testId = "office-map",
  rounded = "rounded-3xl",
}) {
  const query = "Yastık+Medya+Nispetiye+Mah+Aytar+Cad+Bilgin+Sok+Beşiktaş+İstanbul";
  const profileHref = "https://share.google/zzJw0RYdB8OqzT4nZ";

  return (
    <a
      href={profileHref}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label="Yastık Medya ofis · Google Maps'te aç"
      className={`map-embed-creative group relative block ${rounded} ${className}`}
      style={{ perspective: "1200px" }}
    >
      {/* Animated gradient border ring */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute -inset-[2px] ${rounded} map-border-ring`}
      />

      {/* Inner card with the map */}
      <div
        className={`relative ${rounded} overflow-hidden bg-mist border border-ink/10
                    transition-transform duration-500 ease-out
                    group-hover:[transform:rotateX(2deg)_rotateY(-2deg)_scale(1.012)]`}
        style={{ height: `${height}px` }}
      >
        <div
          className="absolute inset-0 transition-[filter] duration-500 group-hover:[filter:contrast(1.02)_saturate(1.1)]"
          style={{ filter: "saturate(0.85) contrast(1.0) brightness(1.02)" }}
        >
          <iframe
            title="Yastık Medya office"
            src={`https://www.google.com/maps?q=${query}&output=embed&z=15`}
            width="100%"
            height="100%"
            style={{ border: 0, display: "block", pointerEvents: "none", width: "100%", height: "100%" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Soft pink/teal blend wash */}
        <div
          className="pointer-events-none absolute inset-0 mix-blend-multiply opacity-35"
          style={{
            background:
              "radial-gradient(circle at 30% 35%, rgba(85,217,194,0.18) 0%, transparent 55%), " +
              "radial-gradient(circle at 70% 60%, rgba(238,79,122,0.25) 0%, transparent 55%)",
          }}
          aria-hidden="true"
        />

        {/* Custom marker — pink rocket + pulsing rings */}
        <div
          className="pointer-events-none absolute"
          style={{ left: "50%", top: "48%", transform: "translate(-50%, -100%)" }}
        >
          <div className="relative flex flex-col items-center">
            {/* 3 pulse rings */}
            <span className="absolute inset-0 m-auto h-16 w-16 -top-3 -translate-x-1/2 left-1/2 rounded-full bg-brand-pink/25 animate-ping-slow" />
            <span className="absolute inset-0 m-auto h-12 w-12 -top-1 -translate-x-1/2 left-1/2 rounded-full bg-brand-pink/35 animate-ping-slower" />
            <span className="absolute inset-0 m-auto h-9 w-9 translate-y-1 -translate-x-1/2 left-1/2 rounded-full bg-brand-pink/55 blur-[2px]" />
            {/* Marker */}
            <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-brand-pink text-cream
                            shadow-[0_14px_38px_-8px_rgba(238,79,122,0.85)] ring-4 ring-cream/70">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"
                aria-hidden="true">
                {/* Lucide-style rocket glyph */}
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z"/>
                <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
              </svg>
            </div>
            {/* Pin shadow on ground */}
            <span className="absolute top-full mt-1 h-1.5 w-6 rounded-full bg-brand-pink-deep/40 blur-[3px]" />
          </div>
        </div>

        {/* Floating sticker rozetler */}
        <div
          className="absolute top-3 start-3 inline-flex items-center gap-1.5 rounded-full
                     bg-cream/95 backdrop-blur px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em]
                     text-ink shadow-[0_6px_20px_-8px_rgba(0,0,0,0.25)]"
          aria-hidden="true"
        >
          <MapPin className="h-3 w-3 text-brand-pink" strokeWidth={2.5} />
          Beşiktaş HQ
        </div>
        <div
          className="absolute bottom-3 end-3 inline-flex items-center gap-1.5 rounded-full
                     bg-postun-blue text-cream px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em]
                     shadow-[0_6px_20px_-8px_rgba(37,99,235,0.6)]"
          aria-hidden="true"
        >
          <Star className="h-3 w-3 text-postun-yellow fill-postun-yellow" strokeWidth={1.5} />
          5.0 · Google Reviews
        </div>

        {/* Hover hint */}
        <div className="absolute end-3 top-14 inline-flex items-center gap-1 rounded-full bg-ink/90 backdrop-blur
                        px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-cream
                        opacity-0 group-hover:opacity-100 transition-opacity">
          Maps'te aç <ExternalLink className="h-3 w-3" />
        </div>
      </div>
    </a>
  );
}
